import { HttpParams } from '@angular/common/http';
import { Component, ContentChildren, ElementRef, forwardRef, Host, Inject, Input, NgZone, OnDestroy, OnInit, QueryList, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import * as _ from 'lodash';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ErrorMessages, Permissions } from '../../common/constants';
import { SOCKET_TOPIC_THING_LIST } from '../../common/endpoints';
import { PropertyInfo } from '../../common/properties';
import { CustomMatPaginatorIntl } from '../../dashboard-area/shared/custom-mat-paginator-intl.service';
import { BulkUpdateParentThingsResponse, Location, Tag, Thing, ThingDataItem } from '../../model';
import { BulkUpdateType } from '../../model/bulk-update';
import { BulkUpdateResourcesResponseItem } from '../../model/bulk-update-resource-response-item';
import { AbstractExportContextService } from '../../service/abstract-export-context.service';
import { AddButtonContextService, AddButtonResource } from '../../service/add-button-context.service';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { NavigationService } from '../../service/navigation.service';
import { OfflineNavigationService } from '../../service/offline-navigation.service';
import { PropertyService } from '../../service/property.service';
import { RefreshableWidget, RefresherWidgetService } from '../../service/refresher-widget.service';
import { UserThingService } from '../../service/user-thing.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { CompositePartComponent, MessageComponent, MetricDetailComponent, ModalComponent, PropertyComponent } from '../../shared/component';
import { BulkDeleteElementType } from '../../shared/component/bulk-delete-dialog/bulk-delete-dialog.component';
import { BulkParentAssignDialogComponent, BulkParentAssignDialogData } from '../../shared/component/bulk-parent-assign-dialog/bulk-parent-assign-dialog.component';
import { BulkUpdateTagDialogComponent, BulkUpdateTagDialogData } from '../../shared/component/bulk-update-tag-dialog/bulk-update-tag-dialog.component';
import { ListSnackbarButtonType } from '../../shared/component/list-snackbar/list-snackbar.component';
import { ButtonActionValue, CustomTableComponent, CustomTableService } from '../../shared/custom-table';
import { BulkControl, ColumnComponentDisplayMode, DetailsModeType, ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { SearchFieldComponent, SearchTargetType } from '../../shared/search-field/search-field.component';
import { TagService } from '../../shared/tags/tag.service';
import { COMPONENT_DEFINITION_REF } from '../../shared/utility/component-definition-token';
import { ErrorUtility } from '../../utility/error-utility';
import { BulkOperationDialogComponent, BulkOperationDialogData } from '../bulk-operation-dialog/bulk-operation-dialog.component';
import { BulkOperationSelectionDialogComponent, BulkOperationSelectionDialogData } from '../bulk-operation-selection-dialog/bulk-operation-selection-dialog.component';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { ThingListWidgetV2Service } from './thing-list-widget-v2.service';

@Component({
    selector: 'thing-list-widget-v2',
    template: require('./thing-list-widget-v2.component.html'),
    styles: [require('../../shared/list-widget-v2/list-widget-v2.css')],
    providers: [ThingListWidgetV2Service, TagService, { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }]
})
export class ThingListWidgetV2Component extends ListWidgetV2Component<Thing> implements OnInit, OnDestroy, RefreshableWidget {

    @Input() includeUnassigned: boolean;

    @Input() includeAssigned: boolean = true;

    @Input() enableActions = true;

    @Input() searchFields: string[] = ["name", "serialNumber", "customer.name", "customer.code"];

    @Input() hideSearch: boolean;

    @Input() tagDeleteEnabled: boolean;

    @Input() expandableCompositeThings: boolean;

    @Input() showRootThings: boolean;

    @Input() detailsPath: string;

    @Input() detailsMode: DetailsModeType = DetailsModeType.PAGE;

    @ContentChildren(COMPONENT_DEFINITION_REF) private columnComponents: QueryList<MetricDetailComponent | CompositePartComponent | PropertyComponent>;

    @ViewChild('saveMessage') saveMessage: MessageComponent;

    @ViewChild('confirmDeleteTag') confirmDeleteTag: ModalComponent;

    @ViewChild(CustomTableComponent) customTable: CustomTableComponent;

    things: Thing[];
    location: Location;
    currentThing: Thing;
    message: string;
    bulkControls: BulkControl[] = [];
    elementExpandableCondition: { path: string, condition: string, value?: any } = { path: 'thingCount', condition: 'GREATER', value: 0 };

    private thingsSubjects: { [thingId: string]: BehaviorSubject<ThingDataItem[]> } = {};
    private metricNames = new Set<string>();
    private unregisterFn: Function;
    private deletingTagThingId: string;
    private tags: Tag[];
    constructor(
        @Inject(forwardRef(() => ThingListWidgetV2Service)) private thingListWidgetService: ThingListWidgetV2Service,
        @Inject(forwardRef(() => AbstractThingContextService)) @Host() private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => PropertyService)) private propertyService: PropertyService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => RefresherWidgetService)) private refresherWidgetService: RefresherWidgetService,
        @Inject(forwardRef(() => TagService)) private tagService: TagService,
        @Inject(forwardRef(() => UserThingService)) private userThingService: UserThingService,
        @Inject(forwardRef(() => OfflineNavigationService)) private offlineNavigationService: OfflineNavigationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => AbstractExportContextService)) exportService: AbstractExportContextService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => AddButtonContextService)) addButtonService: AddButtonContextService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService,
    ) {
        super(appService, authenticationService, exportService, addButtonService, dialog, snackbarService, null, elementRef, coordinator);
    }

    private defaultProperties: { [name: string]: PropertyInfo } = {
        name: { label: 'thingNameProperty', path: 'name', defaultFilter: null, defaultSorting: null },
        serialNumber: { label: 'serialNumberProperty', path: 'serialNumber', defaultFilter: null, defaultSorting: null },
        gpsPosition: { label: 'gpsPositionProperty', path: 'gpsPosition', defaultFilter: null, defaultSorting: null },
        serviceLevel: { label: 'serviceLevelProperty', path: 'serviceLevel', defaultFilter: 'defaultServiceLevel', defaultSorting: 'service-level-sorting' },
        "customer.name": { label: 'customerNameProperty', path: 'customer.name', defaultFilter: null, defaultSorting: null },
        "location.name": { label: 'locationNameProperty', path: 'location.name', defaultFilter: null, defaultSorting: null }
    };

    ngOnInit(): void {
        this.searchFieldId = 'thing-list-query';
        this.popupSearchTarget = SearchTargetType.THINGS;
        this.checkIsMobile();
        this.readPermission = this.authenticationService.hasPermission(Permissions.READ_THING) || this.authenticationService.isGuestUser();
        this.writePermission = this.authenticationService.hasPermission(Permissions.WRITE_THING);
        this.location = this.contextService.getCurrentLocation();
        this.unregisterFn = this.refresherWidgetService.register(this);
        this.currentThing = this.thingContextService.getCurrentThing();
        this.showAddButton = this.writePermission && this.enableActions && (this.location || this.authenticationService.getUser().location) && this.includeAssigned && !this.currentThing;
        if (this.exportEnabled) {
            this.subscribeToExportServices();
        }
        this.preserveSelectedBetweenPages = true;
        if (this.showAddButton) {
            this.subscribeToAddButtonVisibility(AddButtonResource.THING);
        }
    }

    ngAfterContentInit(): void {
        this.tags = this.contextService.getTagObjects();
        this.initVisibleColumns();
        setTimeout(() => {      //waits for search field init
            this.getSearchFieldInputs();
            this.initCompleted = true;
            if (!this.isOnline()) {
                this.executeAdvancedSearch(null);
            }
        });
    }

    protected initVisibleColumns(): void {
        const components = this.columnComponents.toArray().filter(col => !(col instanceof SearchFieldComponent));
        this.columnComponentsArray = this.thingListWidgetService.getColumnComponentsArray(components, this.defaultProperties);
        this.displayedColumns = this.thingListWidgetService.getVisibleColumns(this.columnComponentsArray, this.defaultProperties, 'Thing', this.id);
        if (this.tagDeleteEnabled && this.query?.length && 'tags' == this.query[0].property) {
            this.displayedColumns.push(CustomTableService.newButtonColumn('deleteTag', '', 'id', 'float-right', 'removeButton').withMatIcon('label_off').withColumnClass('no-print'))
        }
        this.editTableColumnsEnabled = !!this.id && this.columnComponentsArray.filter(c => c.displayMode != ColumnComponentDisplayMode.ALWAYS_HIDDEN).length > 1;
        this.descriptions = this.thingListWidgetService.getColumnDescriptions(components);
        this.metricNames = this.thingListWidgetService.getMetricNames(this.columnComponents);
        if (!this.sort) {
            this.sort = this.thingListWidgetService.setDefaultSort(this.displayedColumns);
        }
    }

    private getThingList(): void {
        this.thingListWidgetService.getPagedList(this.pageIndex, this.pageSize, this.sort, this.metricNames, this.searchFields, this.advancedSearchBody, this.location, this.includeAssigned, this.includeUnassigned, this.currentThing, this.showRootThings).then(pagedList => {
            this.enrichList(pagedList.content).then(() => {
                this.things = pagedList.content;
                this.error = null;
                this.subscribeToThings();
                this.thingListWidgetService.addTags(pagedList.content, this.tags || []);
                this.updateElementList(pagedList);
            });
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private enrichList(things: Thing[]): Promise<void> {
        if (!this.isOnline()) {
            return this.offlineNavigationService.getAllThingData().then(allThingData => {
                things.forEach(t => {
                    const offlineThingData = (allThingData || []).find(d => d.id == t.id);
                    if (offlineThingData) {
                        t['offlineTimestamp'] = offlineThingData.timestamp;
                    } else {
                        t['rowClass'] = 'offline-disabled';
                    }
                });
            });
        }
        return Promise.resolve();
    }

    private subscribeToThings(): void {
        for (let thing of this.things) {
            let thingSubject = this.propertyService.subscribeToThingProperties(thing.id);
            this.thingsSubjects[thing.id] = thingSubject;
            thingSubject.subscribe((thingEvent: ThingDataItem[]) => this.updateProperties(thing.id, thingEvent));
        }
    }

    private updateProperties(thingId: string, thingEvent: ThingDataItem[]): void {
        if (thingEvent) {
            let thing = this.things.find(t => t.id == thingId);
            for (let thingDataItem of thingEvent) {
                _.set(thing, PropertyService.getFieldName(thingDataItem), thingDataItem.value);
            }
            this.zone.run(() => this.dataSource.data = this.things);
        }
    }

    addThing(): void {
        if (this.authenticationService.isLocationUser()) {
            this.navigationService.navigateTo(['/dashboard/thing_details/add']);
        } else {
            this.navigationService.navigateTo(['/dashboard/location_details', this.location.id, 'thing_details', 'add']);
        }
    }

    goToDetail(thing: Thing): void {
        if (this.detailsMode == DetailsModeType.PAGE) {
            if (this.isOnline()) {
                this.navigationService.goToThingDetailPage(thing?.id, this.detailsPath);
            } else {
                this.offlineNavigationService.getThingDataById(thing.id).then(offlineData => {
                    if (offlineData) {
                        this.navigationService.goToThingDetailPage(thing.id, this.detailsPath);
                    } else {
                        this.showSnackbar('accessThingOfflineProperty');
                    }
                });
            }
        }
    }

    ngOnDestroy(): void {
        if (this.unregisterFn) {
            this.unregisterFn();
        }
        this.unsubscribeAll();
        super.ngOnDestroy();
    }

    private unsubscribeAll(): void {
        for (let thingId in this.thingsSubjects) {
            this.propertyService.usubscribeFromThingProperties(thingId);
        }
        this.thingsSubjects = {};
    }

    getRefreshTopic(): string {
        let userId = this.authenticationService.getUser().id;
        return SOCKET_TOPIC_THING_LIST.replace('{userId}', userId);
    }

    refresh(): void {
        setTimeout(() => {
            this.zone.run(() => {
                this.loaded = false;
                this.unsubscribeAll();
                this.getThingList();
            });
        }, 1000);
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.unsubscribeAll();
        this.getThingList();
    }

    export(): void {
        const params = this.thingListWidgetService.getParams(this.metricNames, this.searchFields, this.advancedSearchBody, this.location, this.includeAssigned, this.includeUnassigned, this.currentThing, true, this.showRootThings);
        this.thingListWidgetService.downloadCSV(params, this.exportService.resolveExportFileNamePlaceholders(this.exportFileName) || this.exportService.getDefaultFileName(this.title, "things"));
    }

    protected updateSnackbarButtons(): void {
        if (this.selectedElements.length) {
            const allElementsCount = this.allElementsSelected ? this.length : this.selectedElements.length;
            let dividerPosition = 0;
            this.snackbarButtons = [
                { type: ListSnackbarButtonType.SELECTED_ELEMENTS, label: allElementsCount + '', event: 'unselect' }
            ];
            if (!this.allElementsSelected) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'selectAllButton', event: 'selectAll', icon: 'select_all' });
                dividerPosition++;
            }
            if (this.authenticationService.hasPermission(Permissions.WRITE_THING_TAG)) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'updateTagsProperty', event: 'editTags', icon: 'sell' });
            }
            this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'bulkActionsProperty', event: 'bulkOperationSelection', icon: 'magic_exchange' });
            if (this.authenticationService.hasPermission(Permissions.DELETE_THING)) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'bulkDeleteProperty', event: 'bulkDelete', icon: 'delete' });
            }
            this.dividerIndexes = [dividerPosition];
        } else {
            this.snackbarButtons = null;
            this.dividerIndexes = null;
        }
    }

    doSnackbarAction(event: string): void {
        const params = this.thingListWidgetService.getParams(this.metricNames, this.searchFields, this.advancedSearchBody, this.location, this.includeAssigned, this.includeUnassigned, this.currentThing, false, this.showRootThings);
        switch (event) {
            case 'unselect':
                this.clearSelectedElements();
                break;
            case 'selectAll':
                this.setAllElementsSelected();
                break;
            case 'editTags':
                this.openBulkUpdateTagDialog(params);
                break;
            case 'bulkOperationSelection':
                this.openBulkOperationSelectionDialog(params);
                break;
            case 'bulkDelete':
                this.openBulkDeleteDialog(params, BulkDeleteElementType.THING);
                break;
        }
    }

    execButtonAction(actionValue: ButtonActionValue): void {
        if (actionValue.action == 'deleteTag') {
            this.deletingTagThingId = actionValue.value;
            this.confirmDeleteTag.show();
        }
    }

    deleteTag(): void {
        this.confirmDeleteTag.hide();
        let tagName = this.query[0].value;
        const tagId = this.tags.find(t => t.name == tagName).id;
        this.tagService.removeTagsOnThing(this.deletingTagThingId, [tagId]).then(() => this.refreshElementList());
    }

    cancelDeleteTag(): void {
        this.confirmDeleteTag.hide();
    }

    updateDetails(action: { status: string, index: number }): void {
        if (this.expandableCompositeThings) {
            this.unsubscribeAll();
            if (action.status == 'open') {
                this.userThingService.getRecursivelyAllThings(0, [], null, this.things[action.index].id).then(things => {
                    let arrowStatusesToAdd: string[] = [];
                    things.forEach(t => {
                        t['rowLevel'] = this.things[action.index]['rowLevel'] ? this.things[action.index]['rowLevel'] + 1 : 1;
                        arrowStatusesToAdd.push("closed");
                    });
                    this.things.splice(action.index + 1, 0, ...things);
                    this.elements = _.cloneDeep(this.things);
                    this.dataSource = new MatTableDataSource<Thing>(this.elements);
                    this.error = null;
                    this.subscribeToThings();
                    this.customTable.updateArrowStatuses(action.index + 1, 0, arrowStatusesToAdd);
                }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
            } else {
                let parentIds: string[] = [this.things[action.index].id];
                let i = action.index + 1;
                let elementsToRemove: number = 0;
                while (i < this.things.length && parentIds.includes(this.things[i].parentThingId) && this.things[i]['rowLevel'] != null) {
                    parentIds.push(this.things[i].id);
                    elementsToRemove++;
                    i++;
                }
                this.things.splice(action.index + 1, elementsToRemove);
                this.elements = _.cloneDeep(this.things);
                this.dataSource = new MatTableDataSource<Thing>(this.elements);
                this.subscribeToThings();
                this.customTable.updateArrowStatuses(action.index + 1, elementsToRemove, []);
            }
        }
    }

    protected subscribeToExportServices(): void {
        this.exportId = "thing-list-" + this.getNextId();
        this.exportService.subscribeToExport(this.exportId, this.title || "Things").subscribe(() => this.export());
        this.subscribeToExportVisibility();
    }

    private openBulkOperationDialog(operation: BulkUpdateType, searchParams: any, bulkUpdateItems: BulkUpdateResourcesResponseItem[]): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        const data: BulkOperationDialogData = {
            operationType: operation,
            selectedThingIds: this.selectedElements.map(el => { return el["id"]; }),
            allElementsSelected: this.allElementsSelected,
            searchParams: searchParams,
            areaCoordinates: null,
            selectedCoordinates: null,
            mapTagging: false,
            bulkUpdateItems: bulkUpdateItems
        }
        dialogConfig.data = data;
        firstValueFrom(this.dialog.open(BulkOperationDialogComponent, dialogConfig).afterClosed()).then(result => {
            if (result) {
                if (result == 'NOW') {
                    this.showSnackbar('bulkOperationExecutedProperty');
                } else {
                    this.showSnackbar('bulkOperationScheduledProperty')
                }
            }
        });
    }

    private openBulkUpdateTagDialog(searchParams: any): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        const data: BulkUpdateTagDialogData = {
            selectedThingIds: this.selectedElements.map(el => { return el["id"]; }),
            allElementsSelected: this.allElementsSelected,
            searchParams: searchParams,
            areaCoordinates: null,
            selectedCoordinates: null,
            mapTagging: false
        }
        dialogConfig.data = data;
        firstValueFrom(this.dialog.open(BulkUpdateTagDialogComponent, dialogConfig).afterClosed()).then(result => {
            if (result) {
                this.tags = this.contextService.getTagObjects();
                this.refreshElementList();
            }
        });
    }

    private openBulkOperationSelectionDialog(searchParams: HttpParams): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        const data: BulkOperationSelectionDialogData = {
            selectedThingIds: this.selectedElements.map(el => { return el["id"]; }),
            allElementsSelected: this.allElementsSelected,
            searchParams: searchParams
        }
        dialogConfig.data = data;
        firstValueFrom(this.dialog.open(BulkOperationSelectionDialogComponent, dialogConfig).afterClosed()).then(result => {
            if (result) {
                if (result.operation == 'CHANGE_PARENT_THING') {
                    this.openBulkParentAssign(searchParams, result.bulkOperationItems);
                } else {
                    this.openBulkOperationDialog(result.operation, searchParams, result.bulkOperationItems);
                }
            }
        });
    }

    private openBulkParentAssign(searchParams: HttpParams, bulkUpdateParentThingsResponse: BulkUpdateParentThingsResponse): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        const data: BulkParentAssignDialogData = {
            searchParams: searchParams,
            areaCoordinates: null,
            selectedCoordinates: null,
            mapTagging: false,
            allElementsSelected: this.allElementsSelected,
            bulkUpdateParentThingItem: bulkUpdateParentThingsResponse,
            selectedThingIds: this.selectedElements.map(el => { return el["id"]; })
        }
        dialogConfig.data = data;
        firstValueFrom(this.dialog.open(BulkParentAssignDialogComponent, dialogConfig).afterClosed()).then(result => {
            if (result) {
                this.showSnackbar('updateParentThingMessage');
                this.refreshElementList();
            }
        });
    }

}

